import { useEffect, useState } from 'react'
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FeedbackSnackbar } from '../design/FeedbackSnackbar'
import { BanchatConfigStatus } from '../../data/api/ConfigApi'
import { configManager } from '../../data/managers/ConfigManager'

const emptyStatus: BanchatConfigStatus = {
  configured: false,
  tenantCode: '',
  baseUrl: '',
  brandName: '',
  environment: 'production',
  notificationsEnabled: false,
  banchatApiV1014Enabled: false,
  documentAllowedHosts: [],
  termsUrl: '',
  apiKeyConfigured: false,
  inboundSecretConfigured: false,
  outboundSecretConfigured: false,
}

export function BanchatConnectionSettings() {
  const [status, setStatus] = useState(emptyStatus)
  const [savedEnvironment, setSavedEnvironment] = useState<BanchatConfigStatus['environment']>('production')
  const [apiKey, setApiKey] = useState('')
  const [inboundSecret, setInboundSecret] = useState('')
  const [outboundSecret, setOutboundSecret] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [messageSeverity, setMessageSeverity] = useState<AlertColor>('success')
  const credentialsMissing = Boolean(
    ((!status.apiKeyConfigured || status.environment !== savedEnvironment) && !apiKey.trim()) ||
    ((!status.inboundSecretConfigured || status.environment !== savedEnvironment) && !inboundSecret.trim()) ||
    ((!status.outboundSecretConfigured || status.environment !== savedEnvironment) && !outboundSecret.trim()),
  )
  const showingSavedProfile = status.environment === savedEnvironment
  const credentialStatus = (configured: boolean) => {
    if (!showingSavedProfile) return 'Pendiente para este perfil: ingresa una credencial.'
    return configured
      ? '••••••••••••  Guardada. Déjala vacía para conservarla o ingresa una nueva para reemplazarla.'
      : 'Pendiente: todavía no se ha guardado.'
  }

  const refresh = async () => {
    const current = await configManager.getBanchatConfig()
    if (current) {
      setStatus(current)
      setSavedEnvironment(current.environment)
    }
    setLoading(false)
  }
  useEffect(() => { void refresh() }, [])

  const save = async () => {
    setSaving(true)
    setMessage('')
    const result = await configManager.saveBanchatConfig({
      environment: status.environment,
      apiKey,
      inboundSecret,
      outboundSecret,
      termsUrl: status.termsUrl?.trim() || undefined,
      brandName: status.brandName?.trim() || undefined,
      banchatApiV1014Enabled: status.banchatApiV1014Enabled,
    })
    if (result?.configured) {
      setApiKey('')
      setInboundSecret('')
      setOutboundSecret('')
      setMessage('Conexión de créditos guardada. Las claves no volverán a mostrarse.')
      setMessageSeverity('success')
      await refresh()
    } else {
      setMessage(result?.messageError || 'No se pudo guardar. Revisa los datos de conexión.')
      setMessageSeverity('error')
    }
    setSaving(false)
  }

  const disconnect = async () => {
    setSaving(true)
    const result = await configManager.deleteBanchatConfig()
    if (result && !result.configured) {
      setStatus(emptyStatus)
      setMessage('La conexión de créditos quedó desconectada.')
      setMessageSeverity('success')
    } else {
      setMessage('No se pudo desconectar BanChat.')
      setMessageSeverity('error')
    }
    setSaving(false)
  }

  if (loading) return <CircularProgress size={24} />
  return (
    <Card>
      <CardContent>
        <Typography variant="h6">Integración de créditos</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, mb: 2 }}>
          El workflow “Créditos” define el comportamiento del bot. El perfil conectado determina el servicio y tenant; la marca comercial es la identidad que verá el cliente. Cada perfil usa su endpoint y credenciales propios, cifrados por Lypro.
        </Typography>
        <FeedbackSnackbar open={Boolean(message)} message={message} severity={messageSeverity} onClose={() => setMessage('')} />
        <Stack spacing={1.5}>
          <TextField
            select
            label="Marca / perfil de conexión"
            value={status.environment}
            onChange={(e) => {
              const environment = e.target.value as BanchatConfigStatus['environment']
              setStatus({
                ...status,
                environment,
                tenantCode: environment === 'sandbox' ? 'loans-dev' : 'banchat',
                brandName: environment === 'sandbox' ? 'Loans Dev' : 'BanChat',
                termsUrl: '',
                banchatApiV1014Enabled: false,
              })
              setApiKey('')
              setInboundSecret('')
              setOutboundSecret('')
            }}
            helperText="Selecciona un servicio provisionado por Lypro. Cada perfil usa URL, tenant y credenciales propios; no se comparten secretos entre marcas."
            fullWidth
          >
            <MenuItem value="production">BanChat · producción</MenuItem>
            <MenuItem value="sandbox">Loans Dev · perfil aislado</MenuItem>
          </TextField>
          {status.environment !== savedEnvironment && (
            <Alert severity="warning">
              Cambiaste de perfil. Debes ingresar las credenciales correspondientes a esa marca; Lypro no reutilizará los secretos guardados en el otro perfil. La URL de términos es opcional durante beta. Loans Dev requiere además que su integración y procesos estén habilitados por separado.
            </Alert>
          )}
          <Alert severity={showingSavedProfile && status.apiKeyConfigured && status.inboundSecretConfigured && status.outboundSecretConfigured ? 'success' : 'warning'}>
            Credenciales de este perfil: API key {showingSavedProfile && status.apiKeyConfigured ? 'configurada' : 'pendiente'} · HMAC de API {showingSavedProfile && status.inboundSecretConfigured ? 'configurado' : 'pendiente'} · HMAC de webhooks {showingSavedProfile && status.outboundSecretConfigured ? 'configurado' : 'pendiente'}.
          </Alert>
          <TextField label="Tenant asignado al perfil" value={status.environment === 'sandbox' ? 'loans-dev' : 'banchat'} InputProps={{ readOnly: true }} fullWidth />
          <TextField label="API key" type="password" autoComplete="new-password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={showingSavedProfile && status.apiKeyConfigured ? '••••••••••••' : ''} helperText={credentialStatus(status.apiKeyConfigured)} required={!status.apiKeyConfigured || !showingSavedProfile} fullWidth />
          <TextField label="Secreto HMAC para API (Lypro → servicio de créditos)" type="password" autoComplete="new-password" value={inboundSecret} onChange={(e) => setInboundSecret(e.target.value)} placeholder={showingSavedProfile && status.inboundSecretConfigured ? '••••••••••••' : ''} helperText={credentialStatus(status.inboundSecretConfigured)} required={!status.inboundSecretConfigured || !showingSavedProfile} fullWidth />
          <TextField label="Secreto HMAC para webhooks (servicio → Lypro)" type="password" autoComplete="new-password" value={outboundSecret} onChange={(e) => setOutboundSecret(e.target.value)} placeholder={showingSavedProfile && status.outboundSecretConfigured ? '••••••••••••' : ''} helperText={credentialStatus(status.outboundSecretConfigured)} required={!status.outboundSecretConfigured || !showingSavedProfile} fullWidth />
          <Typography variant="body2" color="text.secondary">
            Dominio autorizado para documentos: {status.documentAllowedHosts.join(', ') || 'storage.googleapis.com'}
          </Typography>
          <TextField label="Nombre comercial que verá el cliente" value={status.brandName} onChange={(e) => setStatus({ ...status, brandName: e.target.value })} helperText="Identidad usada por el bot en sus respuestas; el servicio y tenant los determina el perfil seleccionado." fullWidth />
          <TextField label="URL pública de términos y condiciones (opcional en beta)" value={status.termsUrl} onChange={(e) => setStatus({ ...status, termsUrl: e.target.value })} helperText="Déjala vacía mientras no haya una URL publicada: el registro beta podrá continuar con confirmación explícita. Cuando publiquen los términos, cárgalos aquí para compartir el enlace antes del registro." fullWidth />
          <TextField select label="Funciones de la API de créditos" value={status.banchatApiV1014Enabled ? '1.0.14' : 'base'} onChange={(e) => setStatus({ ...status, banchatApiV1014Enabled: e.target.value === '1.0.14' })} fullWidth>
            <MenuItem value="base">API base</MenuItem>
            <MenuItem value="1.0.14">API 1.0.14 o superior (promesas y preferencias)</MenuItem>
          </TextField>
          {status.notificationsEnabled && (
            <Alert severity="info">Los avisos proactivos ya están configurados por Lypro para esta empresa; esta pantalla no modifica sus plantillas ni aprobaciones.</Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" disabled={saving || credentialsMissing} onClick={() => void save()}>{saving ? 'Guardando…' : 'Guardar conexión'}</Button>
            {status.configured && <Button color="error" disabled={saving} onClick={() => void disconnect()}>Desconectar</Button>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
